// providers/AuthProvider.tsx
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { makeStyles } from "@fluentui/react-components";
import {
    useAppDispatch,
    useAppSelector,
    selectUserLoading,
    selectIsAuthenticated,
    selectIsGuest,
    selectGuestAccessEnabled,
    selectUserError,
    selectUserId,
    initializeUser
} from "../store";
import { useSessionHeartbeat } from "../hooks/useSessionHeartbeat";
import { tokens } from "../theme";
import { brand } from "@brand";

// =============================================================================
// TOKEN SHORTCUTS
// =============================================================================

const t = {
    spacingVerticalXxl: tokens.primitives["spacing-Vertical-XXL"],
    spacingVerticalS: tokens.primitives["spacing-Vertical-S"],
    fontFamilyBase: tokens.primitives["font-Family-Base"],
    fontSizeBase300: tokens.primitives["font-Size-Base-300"],
    fontSizeBase400: tokens.primitives["font-Size-Base-400"],
    fontWeightMedium: tokens.primitives["font-Weight-Medium"],
    neutralPrimary: tokens.semantic["neutral-Primary"],
    neutralTertiaryAlt: tokens.semantic["neutral-TertiaryAlt"],
    themePrimary: tokens.palette["theme-Primary"]
};

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
    container: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        width: "100%",
        gap: t.spacingVerticalXxl
    },

    loadingText: {
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase300,
        color: t.neutralTertiaryAlt,
        marginTop: t.spacingVerticalXxl
    },

    errorContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: t.spacingVerticalXxl,
        textAlign: "center",
        padding: t.spacingVerticalXxl
    },

    errorTitle: {
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase400,
        fontWeight: t.fontWeightMedium as unknown as number,
        color: t.neutralPrimary,
        margin: 0
    },

    errorMessage: {
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase300,
        color: t.neutralTertiaryAlt,
        margin: 0
    }
});

// =============================================================================
// DEFAULT FALLBACKS
// =============================================================================

const LOADING_PHRASES = brand.splash.phrases;

const DefaultLoadingFallback = () => {
    const [phraseIndex, setPhraseIndex] = useState(0);
    const [fadeIn, setFadeIn] = useState(true);

    useEffect(() => {
        const interval = setInterval(() => {
            setFadeIn(false);
            setTimeout(() => {
                setPhraseIndex(prev => (prev + 1) % LOADING_PHRASES.length);
                setFadeIn(true);
            }, 300);
        }, 2200);
        return () => clearInterval(interval);
    }, []);

    return (
        <div style={splashStyles.container}>
            {/* Brand mark */}
            <div style={splashStyles.iconWrap}>
                <brand.splash.Mark />
            </div>

            {/* Cycling status text */}
            <div style={splashStyles.textWrap}>
                <span style={{ ...splashStyles.phrase, opacity: fadeIn ? 1 : 0 }}>{LOADING_PHRASES[phraseIndex]}</span>
            </div>

            {/* Subtle progress bar */}
            <div style={splashStyles.progressTrack}>
                <div style={splashStyles.progressBar} />
            </div>

            {/* Inject keyframes */}
            <style>{`
        @keyframes bb-bar-slide {
          0% { opacity: 0; transform: translateX(-12px); }
          40% { opacity: 1; transform: translateX(0); }
          100% { opacity: 1; transform: translateX(0); }
        }
        @keyframes bb-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.04); }
        }
        @keyframes bb-progress {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(0%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
        </div>
    );
};

const splashStyles: Record<string, React.CSSProperties> = {
    container: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        width: "100%",
        background: brand.splash.background,
        fontFamily: brand.splash.fontFamily,
        gap: 32,
        overflow: "hidden"
    },
    iconWrap: {
        width: 80,
        height: 80,
        color: brand.splash.accent,
        animation: "bb-pulse 2.5s ease-in-out infinite"
    },
    textWrap: {
        height: 24,
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
    },
    phrase: {
        fontSize: 15,
        fontWeight: 400,
        color: "rgba(255,255,255,0.5)",
        letterSpacing: "0.02em",
        transition: "opacity 0.3s ease"
    },
    progressTrack: {
        width: 120,
        height: 2,
        borderRadius: 1,
        backgroundColor: "rgba(255,255,255,0.06)",
        overflow: "hidden"
    },
    progressBar: {
        width: "100%",
        height: "100%",
        borderRadius: 1,
        background: `linear-gradient(90deg, transparent, ${brand.splash.accent}, transparent)`,
        animation: "bb-progress 1.8s ease-in-out infinite"
    }
};

const DefaultErrorFallback = ({ error }: { error: string }) => {
    const styles = useStyles();

    return (
        <div className={styles.container}>
            <div className={styles.errorContainer}>
                <h1 className={styles.errorTitle}>Unable to Sign In</h1>
                <p className={styles.errorMessage}>{error}</p>
            </div>
        </div>
    );
};

const DefaultGuestDisabledFallback = () => {
    const styles = useStyles();

    return (
        <div className={styles.container}>
            <div className={styles.errorContainer}>
                <h1 className={styles.errorTitle}>Access Unavailable</h1>
                <p className={styles.errorMessage}>
                    Guest access is not enabled for this application. Please sign in with an organizational account or contact your administrator to request
                    access.
                </p>
            </div>
        </div>
    );
};

// =============================================================================
// COMPONENT
// =============================================================================

interface AuthProviderProps {
    children: ReactNode;
    loadingFallback?: ReactNode;
    errorFallback?: ReactNode | ((error: string) => ReactNode);
    unauthenticatedFallback?: ReactNode;
}

export const AuthProvider = ({ children, loadingFallback, errorFallback, unauthenticatedFallback }: AuthProviderProps) => {
    const dispatch = useAppDispatch();
    const isLoading = useAppSelector(selectUserLoading);
    const isAuthenticated = useAppSelector(selectIsAuthenticated);
    const isGuest = useAppSelector(selectIsGuest);
    const guestAccessEnabled = useAppSelector(selectGuestAccessEnabled);
    const error = useAppSelector(selectUserError);
    const userId = useAppSelector(selectUserId) ?? null;

    useEffect(() => {
        void dispatch(initializeUser());
    }, [dispatch]);

    // Start session heartbeats once user is resolved (no-ops while userId is null)
    useSessionHeartbeat(userId);

    // Minimum splash duration so the loading screen is visible
    const [minDelayDone, setMinDelayDone] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => setMinDelayDone(true), 1000);
        return () => clearTimeout(timer);
    }, []);

    // 1. Still loading (or minimum splash delay hasn't passed)
    if (isLoading || !minDelayDone) {
        return <>{loadingFallback ?? <DefaultLoadingFallback />}</>;
    }

    // 2. Initialization error
    if (error && !isAuthenticated) {
        if (errorFallback) {
            return <>{typeof errorFallback === "function" ? errorFallback(error) : errorFallback}</>;
        }
        return <DefaultErrorFallback error={error} />;
    }

    // 3. Guest user but guest access is disabled
    if (isGuest && !guestAccessEnabled) {
        return <DefaultGuestDisabledFallback />;
    }

    // 4. Not authenticated with custom fallback
    if (!isAuthenticated && unauthenticatedFallback) {
        return <>{unauthenticatedFallback}</>;
    }

    // 5. Render the app
    return <>{children}</>;
};
